import { useState, useEffect, useCallback } from "react";
import api, { imageSrc } from "@/lib/api";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Eyebrow } from "@/components/Eyebrow";
import { EmptyState } from "@/components/EmptyState";
import { copyCaption, exportGroup } from "@/lib/util";
import { Copy, Download, FolderOpen, Layers, ImageIcon } from "lucide-react";

const cardBtn = {
  display: "flex", alignItems: "center", gap: 5, background: "transparent",
  border: "1px solid rgba(24, 24, 27,0.20)", color: "var(--tsecondary)",
  borderRadius: 6, padding: "5px 9px", fontSize: 11, cursor: "pointer", flex: 1, justifyContent: "center",
};

function ContentCard({ group, onOpenBrand }) {
  const cover = (group.items || [])[0];
  const multi = (group.items || []).length > 1;
  return (
    <div className="panel-card" data-testid={`content-card-${group.groupId}`} style={{ padding: 8, overflow: "hidden" }}>
      <div style={{ borderRadius: 8, overflow: "hidden", height: 130, background: "var(--input)" }}>
        {cover && <img src={imageSrc(cover.url)} alt={group.title} loading="lazy" style={{ width: "100%", height: 130, objectFit: "cover" }} />}
      </div>
      <div className="font-display" style={{ fontSize: 13, fontWeight: 600, color: "var(--tprimary)", marginTop: 8, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {group.title || group.assetType}
      </div>
      <div className="font-mono" style={{ fontSize: 10, color: "var(--tmuted)", marginTop: 3, display: "flex", justifyContent: "space-between", gap: 6 }}>
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{group.brandName}</span>
        <span style={{ flexShrink: 0 }}>{group.assetType}{multi ? ` · ${group.items.length}` : ""}</span>
      </div>
      <div style={{ display: "flex", gap: 7, marginTop: 10 }}>
        <button data-testid={`content-copy-${group.groupId}`} className="font-mono" style={cardBtn} onClick={() => copyCaption(group.caption)}>
          <Copy size={11} /> Copy
        </button>
        <button data-testid={`content-export-${group.groupId}`} className="font-mono" style={cardBtn} onClick={() => exportGroup(group)}>
          <Download size={11} /> Export
        </button>
        <button data-testid={`content-open-${group.groupId}`} className="font-mono" style={cardBtn} onClick={() => onOpenBrand(group.brandId)}>
          <FolderOpen size={11} /> Open
        </button>
      </div>
    </div>
  );
}

export function SavedContentSheet({ open, onOpenChange, onOpenBrand }) {
  const [content, setContent] = useState([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");

  const load = useCallback(() => {
    setLoading(true);
    api.get("/content")
      .then(({ data }) => setContent(data.content || []))
      .catch(() => setContent([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { if (open) load(); }, [open, load]);

  const q = query.trim().toLowerCase();
  const filtered = q
    ? content.filter((c) => `${c.title} ${c.brandName} ${c.assetType} ${c.caption}`.toLowerCase().includes(q))
    : content;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="thin-scroll"
        style={{ background: "var(--panel)", borderLeft: "1px solid var(--line)", width: 420, maxWidth: "92vw", overflowY: "auto" }}
        data-testid="content-library-sheet"
      >
        <SheetHeader>
          <SheetTitle asChild><div><Eyebrow>Content Library</Eyebrow></div></SheetTitle>
          <SheetDescription className="sr-only">Browse, copy, export and reuse every asset you've rendered across all brands.</SheetDescription>
        </SheetHeader>

        <input
          data-testid="content-search"
          className="dim-input font-body"
          style={{ marginTop: 18 }}
          placeholder="Search assets by title, brand or type..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />

        {loading && (
          <div className="font-mono" style={{ fontSize: 12, color: "var(--tmuted)", marginTop: 18 }}>// loading library...</div>
        )}

        {!loading && content.length === 0 && (
          <EmptyState
            testId="content-empty"
            icon={Layers}
            title="No saved content yet"
            hint="Every asset you render is saved here automatically. Generate ideas and render assets to start building your library."
          />
        )}

        {!loading && content.length > 0 && filtered.length === 0 && (
          <div className="font-mono" style={{ fontSize: 12, color: "var(--tmuted)", marginTop: 18 }} data-testid="content-no-results">// no assets match "{query}"</div>
        )}

        {!loading && filtered.length > 0 && (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 16 }} data-testid="content-grid">
            {filtered.map((g) => (
              <ContentCard key={g.groupId} group={g} onOpenBrand={onOpenBrand} />
            ))}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
